import { useCallback, useState } from 'react';

import { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { Role } from 'models/Role';

import { RoleService } from 'services/RoleService';

import { RoleFormPayload } from '../types';

interface UseRoleModalsParams {
  message: MessageInstance;
  fetchDataFromServer: (withLoading?: boolean) => Promise<void>;
  setTableLoading: (loading: boolean) => void;
}

export interface UseRoleModalsReturn {
  isNewRoleModalOpen: boolean;
  isDetailModalOpen: boolean;
  isMenuDetailModalOpen: boolean;
  selectedRow: Role | undefined;
  openNewRoleModal: () => void;
  closeNewRoleModal: () => void;
  openDetailModal: (row: Role) => void;
  closeDetailModal: () => void;
  openMenuDetailModal: (row: Role) => void;
  closeMenuDetailModal: () => void;
  createNewRole: (payload: RoleFormPayload) => Promise<void>;
  editRole: (id: number, payload: RoleFormPayload) => Promise<void>;
}

export function useRoleModals({
  message,
  fetchDataFromServer,
  setTableLoading,
}: UseRoleModalsParams): UseRoleModalsReturn {
  const { t } = useTranslation();

  const [isNewRoleModalOpen, setIsNewRoleModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isMenuDetailModalOpen, setIsMenuDetailModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Role | undefined>(undefined);

  // --- open / close handlers ---

  const openNewRoleModal = useCallback(() => {
    setIsNewRoleModalOpen(true);
  }, []);

  const closeNewRoleModal = useCallback(() => {
    setIsNewRoleModalOpen(false);
  }, []);

  const openDetailModal = useCallback((row: Role) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  }, []);

  const openMenuDetailModal = useCallback((row: Role) => {
    setSelectedRow(row);
    setIsMenuDetailModalOpen(true);
  }, []);

  const closeMenuDetailModal = useCallback(() => {
    setSelectedRow(undefined);
    setIsMenuDetailModalOpen(false);
  }, []);

  // --- async actions ---

  const createNewRole = useCallback(
    async (payload: RoleFormPayload) => {
      setTableLoading(true);

      const response = await RoleService.create(payload);

      if (response.success) {
        await fetchDataFromServer(true);
        message.success(
          t('scenes.settings.roles.scene.messages.create_role_success', {
            name: payload.name,
            id: (response.data as { createdId: number }).createdId,
          })
        );
        closeNewRoleModal();
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t('scenes.settings.roles.scene.errors.create_role_failed'),
          3.5
        );
      }

      setTableLoading(false);
    },
    [message, t, fetchDataFromServer, setTableLoading, closeNewRoleModal]
  );

  const editRole = useCallback(
    async (id: number, payload: RoleFormPayload) => {
      setTableLoading(true);

      const response = await RoleService.update(id, payload);

      if (response.success) {
        await fetchDataFromServer(true);
        message.success(
          t('scenes.settings.roles.scene.messages.update_role_success', {
            name: payload.name,
            id,
          })
        );
        closeDetailModal();
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t('scenes.settings.roles.scene.errors.update_role_failed'),
          3.5
        );
      }

      setTableLoading(false);
    },
    [message, t, fetchDataFromServer, setTableLoading, closeDetailModal]
  );

  return {
    isNewRoleModalOpen,
    isDetailModalOpen,
    isMenuDetailModalOpen,
    selectedRow,
    openNewRoleModal,
    closeNewRoleModal,
    openDetailModal,
    closeDetailModal,
    openMenuDetailModal,
    closeMenuDetailModal,
    createNewRole,
    editRole,
  };
}
